package dev.lingvo.site.state

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ServerFeaturesDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionRequestIdentity
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.AppRoute
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope

private data class ProjectRouteKey(
    val session: SessionRequestIdentity,
    val routeSlug: String,
)

/** Live browser authority for one project-detail request. Workspace selection is deliberately absent. */
internal data class ProjectRouteClaim(
    val session: SessionRequestIdentity,
    val routeSlug: String,
)

internal data class VerifiedProjectRoute(
    val workspace: WorkspaceRequestContext,
    val project: ProjectDetailDto,
)

internal sealed interface ProjectRouteResolution {
    data object Loading : ProjectRouteResolution

    data class Verified(val context: VerifiedProjectRoute) : ProjectRouteResolution

    data class Refused(
        val reason: String,
        val retryable: Boolean = false,
    ) : ProjectRouteResolution
}

/**
 * Session-generation single-flight and verified-detail cache for project routes.
 *
 * A route slug alone is not authority. Resolution requires the response slug, its owning org id,
 * its optional legacy org slug, and a membership from the same authenticated generation to agree.
 */
internal class ProjectRouteResolver(
    private val loader: suspend (String) -> ProjectDetailDto,
) {
    private val cache = mutableMapOf<ProjectRouteKey, VerifiedProjectRoute>()
    private val inFlight = mutableMapOf<ProjectRouteKey, CompletableDeferred<ProjectRouteResolution>>()
    private var retainedSession: SessionRequestIdentity? = null

    suspend fun resolve(
        session: SessionRequestIdentity,
        memberships: List<WorkspaceDto>,
        serverFeatures: ServerFeaturesDto,
        routeSlug: String,
        currentClaim: () -> ProjectRouteClaim?,
    ): ProjectRouteResolution = load(
        session = session,
        memberships = memberships,
        serverFeatures = serverFeatures,
        routeSlug = routeSlug,
        expectedWorkspace = null,
        useCache = true,
        currentClaim = currentClaim,
    )

    suspend fun refresh(
        context: WorkspaceRequestContext,
        memberships: List<WorkspaceDto>,
        routeSlug: String,
        currentClaim: () -> ProjectRouteClaim?,
    ): ProjectRouteResolution = load(
        session = context.session,
        memberships = memberships,
        serverFeatures = context.serverFeatures,
        routeSlug = routeSlug,
        expectedWorkspace = context.key,
        useCache = false,
        currentClaim = currentClaim,
    )

    fun seed(context: WorkspaceRequestContext, project: ProjectDetailDto): Boolean {
        if (retainedSession != null && retainedSession != context.session) return false
        val normalized = context.normalizeProject(project) ?: return false
        val key = ProjectRouteKey(context.session, normalized.slug)
        // A server-created detail is newer than an earlier request for the same route. Removing the
        // request identity prevents that older completion from replacing the handoff cache.
        inFlight.remove(key)
        cache[key] = VerifiedProjectRoute(context, normalized)
        return true
    }

    private suspend fun load(
        session: SessionRequestIdentity,
        memberships: List<WorkspaceDto>,
        serverFeatures: ServerFeaturesDto,
        routeSlug: String,
        expectedWorkspace: WorkspaceRequestKey?,
        useCache: Boolean,
        currentClaim: () -> ProjectRouteClaim?,
    ): ProjectRouteResolution {
        if (routeSlug.isBlank()) return ProjectRouteResolution.Refused(tr("state.project_route_context.this_project_link_is_incomplete", "This project link is incomplete."))
        val requestClaim = ProjectRouteClaim(session, routeSlug)
        if (currentClaim() != requestClaim) return staleProjectRouteRefusal()
        if (retainedSession != null && retainedSession != session) {
            return ProjectRouteResolution.Refused(tr("state.project_route_context.your_session_changed_before_this_project", "Your session changed before this project was verified."))
        }
        val key = ProjectRouteKey(session, routeSlug)
        if (useCache) cache[key]?.let {
            return if (currentClaim() == requestClaim) {
                ProjectRouteResolution.Verified(it)
            } else {
                staleProjectRouteRefusal()
            }
        }
        inFlight[key]?.let { shared ->
            val resolution = shared.await()
            return if (currentClaim() == requestClaim) resolution else staleProjectRouteRefusal()
        }

        val pending = CompletableDeferred<ProjectRouteResolution>()
        inFlight[key] = pending
        return try {
            val project = loader(routeSlug)
            if (currentClaim() != requestClaim) {
                val refusal = staleProjectRouteRefusal()
                pending.complete(refusal)
                return refusal
            }
            val verified = verifyProjectRoute(
                session = session,
                memberships = memberships,
                serverFeatures = serverFeatures,
                routeSlug = routeSlug,
                project = project,
            )
            var resolution = if (
                verified is ProjectRouteResolution.Verified &&
                expectedWorkspace != null &&
                verified.context.workspace.key != expectedWorkspace
            ) {
                ProjectRouteResolution.Refused(tr("state.project_route_context.the_refreshed_project_moved_outside_the", "The refreshed project moved outside the verified workspace."))
            } else {
                verified
            }
            val stillCurrent = (retainedSession == null || retainedSession == session) &&
                inFlight[key] === pending &&
                currentClaim() == requestClaim
            if (resolution is ProjectRouteResolution.Verified && !stillCurrent) {
                resolution = ProjectRouteResolution.Refused(
                    tr("state.project_route_context.this_project_verification_was_replaced_by", "This project verification was replaced by a newer session or route request."),
                )
            }
            if (
                resolution is ProjectRouteResolution.Verified &&
                stillCurrent
            ) {
                cache[key] = resolution.context
            }
            pending.complete(resolution)
            resolution
        } catch (cancelled: CancellationException) {
            pending.completeExceptionally(cancelled)
            throw cancelled
        } catch (failure: Throwable) {
            val refusal = ProjectRouteResolution.Refused(
                reason = failure.message?.takeIf(String::isNotBlank)
                    ?: tr(
                        "state.project_route_context.couldnt_verify_this_project_check_your",
                        "Couldn't verify this project. Check your connection and retry.",
                    ),
                retryable = failure.isRetryableRouteFailure(),
            )
            pending.complete(refusal)
            refusal
        } finally {
            if (inFlight[key] === pending) inFlight.remove(key)
        }
    }

    fun cached(session: SessionRequestIdentity, routeSlug: String): VerifiedProjectRoute? =
        cache[ProjectRouteKey(session, routeSlug)]

    fun isLoading(session: SessionRequestIdentity, routeSlug: String): Boolean =
        ProjectRouteKey(session, routeSlug) in inFlight

    fun retainOnly(session: SessionRequestIdentity) {
        retainedSession = session
        cache.keys.removeAll { it.session != session }
        inFlight.keys.removeAll { it.session != session }
    }
}

internal fun verifyProjectRoute(
    session: SessionRequestIdentity,
    memberships: List<WorkspaceDto>,
    serverFeatures: ServerFeaturesDto,
    routeSlug: String,
    project: ProjectDetailDto,
): ProjectRouteResolution {
    if (project.slug != routeSlug) {
        return ProjectRouteResolution.Refused(tr("state.project_route_context.the_project_response_did_not_match", "The project response did not match this link."))
    }
    val workspace = memberships.firstOrNull { it.id == project.orgId }
        ?: return ProjectRouteResolution.Refused(tr("state.project_route_context.this_project_is_not_in_one", "This project is not in one of your workspaces."))
    if (project.orgSlug.isNotBlank() && project.orgSlug != workspace.slug) {
        return ProjectRouteResolution.Refused(tr("state.project_route_context.the_project_workspace_identity_was_contradictory", "The project workspace identity was contradictory."))
    }
    val context = WorkspaceRequestContext.resolve(
        session = session,
        memberships = memberships,
        selectedSlug = workspace.slug,
        serverFeatures = serverFeatures,
    ) ?: return ProjectRouteResolution.Refused(tr("state.project_route_context.this_project_workspace_is_no_longer", "This project workspace is no longer available."))
    if (!context.acceptsProject(project)) {
        return ProjectRouteResolution.Refused(tr("state.project_route_context.the_project_did_not_belong_to", "The project did not belong to the verified workspace."))
    }
    val normalizedProject = if (project.orgSlug.isBlank()) project.copy(orgSlug = workspace.slug) else project
    return ProjectRouteResolution.Verified(VerifiedProjectRoute(context, normalizedProject))
}

internal fun seedProjectRouteForSession(
    resolver: ProjectRouteResolver,
    currentSession: SessionRequestIdentity?,
    context: WorkspaceRequestContext,
    project: ProjectDetailDto,
): Boolean {
    if (currentSession != context.session) return false
    resolver.retainOnly(context.session)
    return resolver.seed(context, project)
}

internal fun projectRouteClaim(
    session: SessionRequestIdentity?,
    pathname: String,
): ProjectRouteClaim? {
    val requestSession = session ?: return null
    val routeSlug = AppRoute.projectSlug(pathname) ?: return null
    return ProjectRouteClaim(requestSession, routeSlug)
}

/** Mounted by both layouts before either shell, trail, or child action exists. */
@Composable
internal fun ProjectRouteGate(
    routeSlug: String,
    session: SessionRequestIdentity,
    memberships: List<WorkspaceDto>,
    serverFeatures: ServerFeaturesDto,
    resolver: ProjectRouteResolver,
    currentClaim: () -> ProjectRouteClaim?,
    onAdoptWorkspace: (String) -> Unit,
    onSeedProject: (WorkspaceRequestContext, ProjectDetailDto) -> Unit,
    onRefreshDirectory: (WorkspaceRequestContext) -> Unit,
    loading: @Composable () -> Unit,
    refused: @Composable (ProjectRouteResolution.Refused, () -> Unit) -> Unit,
    content: @Composable (VerifiedProjectRoute) -> Unit,
) {
    val requestKey = remember(session, routeSlug) { session to routeSlug }
    val requestClaim = remember(session, routeSlug) { ProjectRouteClaim(session, routeSlug) }
    var resolution by remember(resolver, requestKey) {
        mutableStateOf<ProjectRouteResolution>(ProjectRouteResolution.Loading)
    }
    var retryKey by remember(resolver, requestKey) { mutableStateOf(0) }
    LaunchedEffect(resolver, requestKey, memberships, serverFeatures, retryKey) {
        if (currentClaim() != requestClaim) return@LaunchedEffect
        resolution = ProjectRouteResolution.Loading
        val settled = resolver.resolve(
            session = session,
            memberships = memberships,
            serverFeatures = serverFeatures,
            routeSlug = routeSlug,
            currentClaim = currentClaim,
        )
        // Session Loading/logout and history changes are synchronous; composition retirement is
        // not. Re-read the live browser authority after the await and before every side effect.
        if (currentClaim() != requestClaim) return@LaunchedEffect
        when (settled) {
            is ProjectRouteResolution.Verified -> {
                onAdoptWorkspace(settled.context.workspace.workspace.slug)
                onSeedProject(settled.context.workspace, settled.context.project)
                onRefreshDirectory(settled.context.workspace)
                resolution = settled
            }
            else -> resolution = settled
        }
    }

    when (val current = resolution) {
        ProjectRouteResolution.Loading -> loading()
        is ProjectRouteResolution.Refused -> refused(current) { retryKey++ }
        is ProjectRouteResolution.Verified -> content(current.context)
    }
}

/** Production cache and gate wiring shared by AppLayout, EditorLayout, and their children. */
internal object ProjectRouteContext {
    private val resolver = ProjectRouteResolver(ApiClient::getProject)

    private fun liveClaim(): ProjectRouteClaim? =
        projectRouteClaim(Session.requestIdentity, window.location.pathname)

    fun current(routeSlug: String): VerifiedProjectRoute? =
        Session.requestIdentity?.let { resolver.cached(it, routeSlug) }

    fun seed(context: WorkspaceRequestContext, project: ProjectDetailDto): Boolean =
        seedProjectRouteForSession(resolver, Session.requestIdentity, context, project)

    suspend fun refresh(routeSlug: String, directoryScope: CoroutineScope): ProjectDetailDto {
        val session = Session.requestIdentity
            ?: error("Your session changed before the project could refresh.")
        resolver.retainOnly(session)
        val current = resolver.cached(session, routeSlug)
            ?: error("This project has not passed the workspace authority gate.")
        val requestClaim = ProjectRouteClaim(session, routeSlug)
        check(liveClaim() == requestClaim) {
            "The browser moved before the project refresh could start."
        }
        return when (
            val resolution = resolver.refresh(
                context = current.workspace,
                memberships = Session.organizations,
                routeSlug = routeSlug,
                currentClaim = ::liveClaim,
            )
        ) {
            is ProjectRouteResolution.Verified -> {
                check(liveClaim() == requestClaim) {
                    "The session or project route changed before the refresh could be published."
                }
                resolution.context.project.also { project ->
                    ProjectDirectory.seed(resolution.context.workspace, project)
                    ProjectDirectory.ensure(directoryScope, resolution.context.workspace)
                }
            }
            is ProjectRouteResolution.Refused -> error(resolution.reason)
            ProjectRouteResolution.Loading -> error("The project refresh did not finish.")
        }
    }

    @Composable
    fun Gate(
        routeSlug: String,
        loading: @Composable () -> Unit,
        refused: @Composable (ProjectRouteResolution.Refused, () -> Unit) -> Unit,
        content: @Composable (VerifiedProjectRoute) -> Unit,
    ) {
        val session = Session.requestIdentity
        if (session == null) {
            refused(ProjectRouteResolution.Refused(t("state.project_route_context.your_session_is_not_ready_yet", "Your session is not ready yet."))) {}
            return
        }
        val scope = rememberCoroutineScope()
        resolver.retainOnly(session)
        ProjectRouteGate(
            routeSlug = routeSlug,
            session = session,
            memberships = Session.organizations,
            serverFeatures = Session.serverFeatures,
            resolver = resolver,
            currentClaim = ::liveClaim,
            onAdoptWorkspace = WorkspaceSelection::select,
            onSeedProject = { context, project -> ProjectDirectory.seed(context, project) },
            onRefreshDirectory = { context -> ProjectDirectory.ensure(scope, context) },
            loading = loading,
            refused = refused,
            content = content,
        )
    }
}

private fun staleProjectRouteRefusal() = ProjectRouteResolution.Refused(
    tr("state.project_route_context.this_project_verification_was_replaced_by", "This project verification was replaced by a newer session or route request."),
)

private fun Throwable.isRetryableRouteFailure(): Boolean {
    val status = (this as? ApiException)?.status ?: return true
    return status == 0 || status == 408 || status == 429 || status >= 500
}
